import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../../../../config';
import { normalizeMyNumber } from '../../GeneralProfile/useGeneralProfileFlow';
import type { FamilyMemberApi } from '../../family/familyInfoTypes';
import { fetchTaxDeclarationProgress, upsertTaxDeclarationProgress } from '../taxDeclarationApi';
import {
  dependentsCursor,
  fetchDependentCandidates,
  parseDependentsCursor,
  saveDependent,
  type DependentCandidate,
} from './dependentsSectionApi';
import {
  committedStepsForSnapshot,
  deriveIneligibility,
  deriveTaxCategories,
  firstMissingStepFrom,
  nextStepAfter,
  prevStepForUndo,
  stepHelpKey,
  stepQuestionKey,
  userAnswerForStep as stepAnswer,
} from './dependentsSectionSteps';
import { buildDependentSummary } from './dependentsSummary';
import {
  emptyDependentDraft,
  type CompletedDependentEntry,
  type DependentDraft,
  type DependentsStep,
  type YesNo,
} from './dependentsSectionTypes';

export type WheelDateValue = { year: number; month: number; day: number };

function defaultDobWheel(): WheelDateValue {
  const today = new Date();
  return { year: today.getFullYear() - 20, month: 1, day: 1 };
}

function defaultChangeDateWheel(): WheelDateValue {
  const today = new Date();
  return { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };
}

function wheelFromIso(iso: string, fallback: WheelDateValue): WheelDateValue {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return fallback;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

function isoFromWheel(w: WheelDateValue): string {
  return `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`;
}

const KATAKANA_RE = /^[\u30A0-\u30FF\uFF66-\uFF9D\u3000\sー]+$/u;

/** Steps that belong to the per-dependent question loop (vs. section-level steps). */
const MEMBER_STEPS: DependentsStep[] = [
  'addName',
  'addKana',
  'addRelationship',
  'addRelationshipOther',
  'addDob',
  'confirmFullName',
  'confirmFullNameKana',
  'confirmRelationship',
  'myNumber',
  'sameHousehold',
  'addressIfNot',
  'sharedLivelihood',
  'estimatedIncome',
  'ineligibleNotice',
  'ageCategoryNotice',
  'nonResident',
  'nonResidentReason',
  'remittanceAmount',
  'disabilityStatus',
  'changeDuringYear',
  'changeDate',
  'changeReason',
  'changeReasonOther',
];

export function useDependentsSectionFlow(
  email: string | null,
  taxYear: number,
  t: (key: string) => string,
  onSectionDoneChange: (done: boolean) => void
) {
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [step, setStep] = useState<DependentsStep>('hasDependents');
  const [draft, setDraft] = useState<DependentDraft>(emptyDependentDraft);
  const [completed, setCompleted] = useState<CompletedDependentEntry[]>([]);
  const [queue, setQueue] = useState<DependentCandidate[]>([]);
  const [committed, setCommitted] = useState<Set<DependentsStep>>(() => new Set());
  const [hasDependentsAnswer, setHasDependentsAnswer] = useState<YesNo | ''>('');
  const [addAnotherAnswer, setAddAnotherAnswer] = useState<YesNo | ''>('');
  const [addAnotherCommitted, setAddAnotherCommitted] = useState(false);
  const [hasDependentsCommitted, setHasDependentsCommitted] = useState(false);
  /** Completed-entry index expanded for inline review/edit; null = normal flow. */
  const [editIndex, setEditIndex] = useState<number | null>(null);
  /** True while a single field is being re-answered from the expanded review. */
  const [fieldEditMode, setFieldEditMode] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [dob, setDob] = useState<WheelDateValue>(defaultDobWheel);
  const [changeDateWheel, setChangeDateWheel] = useState<WheelDateValue>(defaultChangeDateWheel);
  const originalsRef = useRef<Map<number, FamilyMemberApi>>(new Map());

  const reviewMode = step === 'finalConfirm';
  const editingFromReview = editIndex !== null;

  const markCommitted = useCallback((s: DependentsStep, value = true) => {
    setCommitted((prev) => {
      const next = new Set(prev);
      if (value) next.add(s);
      else next.delete(s);
      return next;
    });
  }, []);

  const isCommitted = useCallback((s: DependentsStep) => committed.has(s), [committed]);

  const resetMemberState = useCallback(() => {
    setCommitted(new Set());
    setDraft(emptyDependentDraft());
    setDob(defaultDobWheel());
    setChangeDateWheel(defaultChangeDateWheel());
  }, []);

  const startDependent = useCallback((candidate: DependentCandidate) => {
    setCommitted(new Set());
    setDraft({ ...candidate.draft, eligible: true, ineligibilityReason: '' });
    setDob(wheelFromIso(candidate.draft.dateOfBirth, defaultDobWheel()));
    setChangeDateWheel(wheelFromIso(candidate.draft.changeDate, defaultChangeDateWheel()));
    setStep('confirmFullName');
  }, []);

  const persistCursor = useCallback(
    async (processedCount: number, sectionDone?: boolean) => {
      if (!email?.trim()) return;
      await upsertTaxDeclarationProgress({
        email,
        taxYear,
        currentStepId: dependentsCursor(processedCount),
        ...(sectionDone !== undefined ? { sectionDependentsDone: sectionDone } : {}),
      });
    },
    [email, taxYear]
  );

  // Load candidates + resume cursor once.
  useEffect(() => {
    if (!email?.trim() || !API_BASE_URL) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    (async () => {
      try {
        const [progress, candidates] = await Promise.all([
          fetchTaxDeclarationProgress(email, taxYear),
          fetchDependentCandidates(email),
        ]);
        if (cancelled) return;
        for (const c of candidates) {
          if (c.original.id != null) originalsRef.current.set(c.original.id, c.original);
        }
        const sectionDone = progress?.sectionDependentsDone === true;
        const cursor = parseDependentsCursor(progress?.currentStepId);
        const processed = sectionDone
          ? candidates.length
          : Math.min(cursor ?? 0, candidates.length);
        const done = candidates.slice(0, processed).map((c) => ({
          text: buildDependentSummary(c.draft, taxYear, t),
          snapshot: c.draft,
        }));
        const rest = candidates.slice(processed);
        setCompleted(done);
        setQueue(rest);
        if (sectionDone) {
          setIsComplete(true);
          setStep('finalConfirm');
        } else if (rest.length > 0) {
          startDependent(rest[0]);
        } else if (done.length > 0) {
          setStep('addAnother');
        } else {
          setStep('hasDependents');
        }
      } catch {
        if (!cancelled) setSaveError(t('submitDocuments.dependentDeduction.dependents.saveFailed'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per email/year; t/startDependent are stable enough
  }, [email, taxYear]);

  const finishCurrentDependent = useCallback(
    async (d: DependentDraft) => {
      if (!email?.trim()) return;
      try {
        const original = d.empFamilyId != null ? originalsRef.current.get(d.empFamilyId) ?? null : null;
        const savedId = await saveDependent({ email, draft: d, original, taxYear });
        const snapshot: DependentDraft = { ...d, empFamilyId: d.empFamilyId ?? savedId };
        const entry: CompletedDependentEntry = {
          text: buildDependentSummary(snapshot, taxYear, t),
          snapshot,
        };
        const nextCompleted = [...completed, entry];
        setCompleted(nextCompleted);
        resetMemberState();
        setSaveError(null);
        await persistCursor(nextCompleted.length);
        const rest = queue.slice(1);
        setQueue(rest);
        if (rest.length > 0) {
          startDependent(rest[0]);
        } else {
          setAddAnotherAnswer('');
          setAddAnotherCommitted(false);
          setStep('addAnother');
        }
      } catch {
        setSaveError(t('submitDocuments.dependentDeduction.dependents.saveFailed'));
      }
    },
    [email, taxYear, t, completed, queue, persistCursor, resetMemberState, startDependent]
  );

  /** Save an isolated field edit and return to the expanded summary review. */
  const finishFieldEdit = useCallback(
    async (d: DependentDraft) => {
      if (!email?.trim() || editIndex === null) return;
      try {
        const original = d.empFamilyId != null ? originalsRef.current.get(d.empFamilyId) ?? null : null;
        const savedId = await saveDependent({ email, draft: d, original, taxYear });
        const snapshot: DependentDraft = { ...d, empFamilyId: d.empFamilyId ?? savedId };
        const entry: CompletedDependentEntry = {
          text: buildDependentSummary(snapshot, taxYear, t),
          snapshot,
        };
        setCompleted((prev) => prev.map((e, i) => (i === editIndex ? entry : e)));
        setDraft(snapshot);
        setCommitted(new Set(committedStepsForSnapshot(snapshot, taxYear)));
        setFieldEditMode(false);
        setSaveError(null);
        setStep('finalConfirm');
      } catch {
        setSaveError(t('submitDocuments.dependentDeduction.dependents.saveFailed'));
      }
    },
    [email, taxYear, t, editIndex]
  );

  /** Commit the current step's answer and advance (handles eligibility gates). */
  const submitStep = useCallback(
    async (fromStep: DependentsStep, patch: Partial<DependentDraft> = {}) => {
      const updated: DependentDraft = { ...draft, ...patch };

      // Clear values (and their committed rows) on branches closed by a changed gate answer.
      if (fromStep === 'addRelationship' && updated.relationship !== 'other') {
        updated.relationshipOther = '';
        markCommitted('addRelationshipOther', false);
      }
      if (fromStep === 'sameHousehold' && updated.livingInSameHousehold === 'yes') {
        updated.ifNotAddress = '';
        markCommitted('addressIfNot', false);
      }
      if (fromStep === 'nonResident' && updated.nonResident === 'no') {
        updated.nonResidentReason = '';
        updated.remittanceAmount = '';
        markCommitted('nonResidentReason', false);
        markCommitted('remittanceAmount', false);
      }
      if (fromStep === 'nonResidentReason' && updated.nonResidentReason !== 'remittance_380k') {
        updated.remittanceAmount = '';
        markCommitted('remittanceAmount', false);
      }
      if (fromStep === 'changeDuringYear' && updated.changeDuringYear === 'no') {
        updated.changeDate = '';
        updated.changeReason = '';
        markCommitted('changeDate', false);
        markCommitted('changeReason', false);
        markCommitted('changeReasonOther', false);
      }

      const reason = deriveIneligibility(updated, taxYear);
      updated.eligible = reason === '';
      updated.ineligibilityReason = reason;

      const next = nextStepAfter(fromStep, updated, taxYear);
      setDraft(updated);
      markCommitted(fromStep);

      if (editIndex !== null && fieldEditMode) {
        // Isolated edit: surface a fresh ineligibility, walk into newly opened
        // branch questions, otherwise save and return to the expanded review.
        if (next === 'ineligibleNotice' || (!updated.eligible && draft.eligible)) {
          setStep('ineligibleNotice');
          return;
        }
        const target = firstMissingStepFrom(next, updated, taxYear);
        if (target === 'addAnother') {
          await finishFieldEdit(updated);
          return;
        }
        setStep(target);
        return;
      }

      if (next === 'addAnother') {
        await finishCurrentDependent(updated);
        return;
      }
      setStep(next);
    },
    [draft, taxYear, markCommitted, editIndex, fieldEditMode, finishFieldEdit, finishCurrentDependent]
  );

  const handleHasDependents = useCallback(
    async (v: YesNo) => {
      setHasDependentsAnswer(v);
      setHasDependentsCommitted(true);
      if (v === 'yes') {
        resetMemberState();
        setDraft({ ...emptyDependentDraft(), addedInSection: true });
        setStep('addName');
        return;
      }
      setStep('finalConfirm');
      setIsComplete(true);
      onSectionDoneChange(true);
      await persistCursor(0, true);
    },
    [resetMemberState, onSectionDoneChange, persistCursor]
  );

  const handleAddAnother = useCallback(
    async (v: YesNo) => {
      setAddAnotherAnswer(v);
      setAddAnotherCommitted(true);
      if (v === 'yes') {
        resetMemberState();
        setDraft({ ...emptyDependentDraft(), addedInSection: true });
        setStep('addName');
        return;
      }
      setStep('finalConfirm');
      setIsComplete(true);
      onSectionDoneChange(true);
      await persistCursor(completed.length, true);
    },
    [resetMemberState, onSectionDoneChange, persistCursor, completed.length]
  );

  /** Expand a completed dependent's summary into its Q&A for inline review/edit. */
  const handleEditSummary = useCallback(
    (index: number) => {
      const entry = completed[index];
      if (!entry) return;
      setEditIndex(index);
      setFieldEditMode(false);
      setDraft({ ...entry.snapshot });
      setCommitted(new Set(committedStepsForSnapshot(entry.snapshot, taxYear)));
      setDob(wheelFromIso(entry.snapshot.dateOfBirth, defaultDobWheel()));
      setChangeDateWheel(wheelFromIso(entry.snapshot.changeDate, defaultChangeDateWheel()));
      setStep('finalConfirm');
    },
    [completed, taxYear]
  );

  /** Re-answer a single question from the expanded review. */
  const beginEditField = useCallback(
    (fieldStep: DependentsStep) => {
      if (editIndex === null) return;
      setFieldEditMode(true);
      setStep(fieldStep);
    },
    [editIndex]
  );

  /** Collapse the expanded review back to the section state it was opened from. */
  const cancelEdit = useCallback(() => {
    setEditIndex(null);
    setFieldEditMode(false);
    resetMemberState();
    setStep(isComplete ? 'finalConfirm' : 'addAnother');
  }, [isComplete, resetMemberState]);

  const handleUndo = useCallback(() => {
    const prev = prevStepForUndo(step, draft);
    if (!prev) return;
    markCommitted(prev, false);
    setStep(prev);
  }, [step, draft, markCommitted]);

  /** Re-enter a completed section from the menu in review mode. */
  const enterReviewFromMenu = useCallback(() => {
    setEditIndex(null);
    setFieldEditMode(false);
    setStep('finalConfirm');
  }, []);

  // ----- composer handlers -----

  const setDraftField = useCallback(
    <K extends keyof DependentDraft>(key: K, value: DependentDraft[K]) => {
      setDraft((d) => ({ ...d, [key]: value }));
    },
    []
  );

  const handleSendDob = useCallback(async () => {
    const iso = isoFromWheel(dob);
    await submitStep('addDob', { dateOfBirth: iso });
  }, [dob, submitStep]);

  const handleSendChangeDate = useCallback(async () => {
    const iso = isoFromWheel(changeDateWheel);
    await submitStep('changeDate', { changeDate: iso });
  }, [changeDateWheel, submitStep]);

  const handleIneligibleOk = useCallback(async () => {
    await submitStep('ineligibleNotice');
  }, [submitStep]);

  const handleAgeCategoryOk = useCallback(async () => {
    await submitStep('ageCategoryNotice');
  }, [submitStep]);

  // ----- validation -----

  const validateRequired = useCallback(
    (v: string) => (v.trim() ? null : t('submitDocuments.profileName.validation.required')),
    [t]
  );

  const validateKana = useCallback(
    (v: string) => {
      const s = v.trim();
      if (!s) return t('submitDocuments.profileName.validation.required');
      if (!KATAKANA_RE.test(s)) return t('submitDocuments.familyInfo.kanaInvalid');
      return null;
    },
    [t]
  );

  const validateMyNumber = useCallback(
    (v: string) => {
      const n = normalizeMyNumber(v);
      if (!n) return t('submitDocuments.profileName.validation.required');
      if (n.length !== 12) return t('submitDocuments.profileName.validation.myNumber');
      return null;
    },
    [t]
  );

  // ----- display helpers -----

  const userAnswerForStep = useCallback(
    (s: DependentsStep) => stepAnswer(s, draft, t),
    [draft, t]
  );

  const helpTextForStep = useCallback(
    (s: DependentsStep) => {
      const key = stepHelpKey(s);
      return key ? t(key) : '';
    },
    [t]
  );

  const derivedCategories = deriveTaxCategories(draft, taxYear);

  const ageCategoryNoticeText = (() => {
    const labels = derivedCategories
      .filter((c) => c !== 'non_residents')
      .map((c) => t(`submitDocuments.dependentDeduction.dependents.category.${c}`));
    if (labels.length === 0) {
      labels.push(t('submitDocuments.dependentDeduction.dependents.category.general'));
    }
    return `${t('submitDocuments.dependentDeduction.dependents.ageCategoryIntro')} ${labels.join(', ')}`;
  })();

  const ineligibleNoticeText = draft.ineligibilityReason
    ? `${t('submitDocuments.dependentDeduction.dependents.ineligibleIntro')} ${t(
        `submitDocuments.dependentDeduction.dependents.ineligibleReason.${draft.ineligibilityReason}`
      )}`
    : '';

  const inMemberFlow = MEMBER_STEPS.includes(step);

  /** Index label of the dependent currently being answered (1-based). */
  const currentDependentNumber =
    editIndex !== null ? editIndex + 1 : completed.length + 1;

  const showEditOnSummary = useCallback(
    (index: number) => {
      if (editIndex !== null) return false;
      if (index < 0 || index >= completed.length) return false;
      return step === 'addAnother' || step === 'finalConfirm';
    },
    [editIndex, completed.length, step]
  );

  const canSendMyNumber = normalizeMyNumber(draft.myNumber).length === 12;

  return {
    t,
    isLoading,
    saveError,
    step,
    draft,
    setDraft,
    setDraftField,
    completed,
    queue,
    isCommitted,
    hasDependentsAnswer,
    hasDependentsCommitted,
    addAnotherAnswer,
    addAnotherCommitted,
    editIndex,
    editingFromReview,
    fieldEditMode,
    /** Expanded summary shown as Q&A with per-field Edit buttons. */
    expandedReview: editIndex !== null && step === 'finalConfirm',
    reviewMode,
    isComplete,
    inMemberFlow,
    currentDependentNumber,
    dob,
    setDob,
    changeDateWheel,
    setChangeDateWheel,
    derivedCategories,
    ageCategoryNoticeText,
    ineligibleNoticeText,
    submitStep,
    handleHasDependents,
    handleAddAnother,
    handleEditSummary,
    beginEditField,
    cancelEdit,
    handleUndo,
    enterReviewFromMenu,
    handleSendDob,
    handleSendChangeDate,
    handleIneligibleOk,
    handleAgeCategoryOk,
    validateRequired,
    validateKana,
    validateMyNumber,
    userAnswerForStep,
    helpTextForStep,
    stepQuestionKey,
    showEditOnSummary,
    canSendMyNumber,
    undoLabel: t('submitDocuments.chat.undo'),
    editLabel: t('submitDocuments.fillFormsChecklist.edit'),
  };
}

export type DependentsSectionFlow = ReturnType<typeof useDependentsSectionFlow>;
